import React, {useState} from 'react';
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import format from "date-fns/format";

import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";


/**
 * Format number as currency string
 */
const formatCurrency = (value, showSign = false) => {
  if (value === 0 || value === null || value === undefined) return '—';
  
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.abs(value));

  if (showSign && value !== 0) {
    return value > 0 ? `+${formatted}` : `-${formatted}`;
  }
  
  return value < 0 ? `-${formatted}` : formatted;
};

/**
 * Editable cell component for inline editing
 */
const EditableCell = ({value, onChange, disabled = false}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState('');

  const handleStartEdit = () => {
    if (!disabled) {
      setTempValue(value ? String(value) : '');
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    const numValue = parseFloat(tempValue) || 0;
    if (numValue !== value) {
      onChange(numValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  if (isEditing && !disabled) {
    return (
      <TextField
        type="number"
        size="small"
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoFocus
        placeholder=""
        sx={{
          width: 100,
          '& input': {
            padding: '4px 8px',
            textAlign: 'right'
          }
        }}
      />
    );
  }

  return (
    <Box
      onClick={handleStartEdit}
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        padding: '4px 8px',
        borderRadius: 1,
        '&:hover': disabled ? {} : {
          backgroundColor: 'action.hover'
        }
      }}
    >
      {formatCurrency(value)}
    </Box>
  );
};

/**
 * Month type chip indicator
 */
const MonthTypeChip = ({type}) => {
  const config = {
    actual: {
      label: 'Actual',
      color: 'success',
      tooltip: 'All payments in this month are paid'
    },
    forecast: {
      label: 'Forecast',
      color: 'warning',
      tooltip: 'All payments in this month are NOT paid'
    },
    partial: {
      label: 'Partial',
      color: 'info',
      tooltip: 'This month contains both paid and unpaid payments'
    },
    sales_only: {
      label: 'Sales Only',
      color: 'default',
      tooltip: 'Only sales data available for this month'
    }
  };

  const {label, color, tooltip} = config[type] || config.sales_only;

  return (
    <Tooltip title={tooltip} arrow>
      <Chip
        label={label}
        size="small"
        color={color}
        variant="outlined"
        sx={{height: 20, fontSize: '10px'}}
      />
    </Tooltip>
  );
};

/**
 * Main cashflow table component
 */
const CashflowTable = ({rows = [], totals = {}, onOverrideChange, onAddMonth}) => {
  const handleSalesChange = (month, value) => {
    onOverrideChange(month, 'sales', value);
  };

  const handleExpensesChange = (month, value) => {
    onOverrideChange(month, 'expenses', -Math.abs(value));
  };

  const handleIncomeChange = (month, value) => {
    onOverrideChange(month, 'income', value);
  };

  const handleAddMonthBefore = () => {
    if (rows.length > 0) {
      const firstMonth = new Date(rows[0].month);
      firstMonth.setMonth(firstMonth.getMonth() - 1);
      const newMonthKey = firstMonth.toISOString().slice(0, 7) + '-01';
      onAddMonth(newMonthKey, 'before');
    }
  };

  const handleAddMonthAfter = () => {
    if (rows.length > 0) {
      const lastMonth = new Date(rows[rows.length - 1].month);
      lastMonth.setMonth(lastMonth.getMonth() + 1);
      const newMonthKey = lastMonth.toISOString().slice(0, 7) + '-01';
      onAddMonth(newMonthKey, 'after');
    }
  };

  return (
    <Card>
      {/* Add button
      <Box sx={{display: 'flex', justifyContent: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider'}}>
        <Tooltip title="Add month before">
          <IconButton size="small" onClick={handleAddMonthBefore}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      */}

      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{fontWeight: 600, backgroundColor: 'background.paper'}}>
                Month
              </TableCell>
              <TableCell align="right" sx={{fontWeight: 600, backgroundColor: 'background.paper'}}>
                Sales (Out)
              </TableCell>
              <TableCell align="right" sx={{fontWeight: 600, backgroundColor: 'background.paper'}}>
                Expenses
              </TableCell>
              <TableCell align="right" sx={{fontWeight: 600, backgroundColor: 'background.paper'}}>
                Total Income
              </TableCell>
              <TableCell align="right" sx={{fontWeight: 600, backgroundColor: 'background.paper'}}>
                Cashflow
              </TableCell>
              <TableCell align="center" sx={{fontWeight: 600, backgroundColor: 'background.paper'}}>
                Type
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const isFirstRow = index === 0;
              const monthFormatted = row.month 
                ? format(new Date(row.month), 'MMM yyyy')
                : 'Unknown';

              return (
                <TableRow
                  key={row.month || index}
                  hover
                  sx={{
                    backgroundColor: row.month_type === 'forecast' 
                      ? 'action.hover' 
                      : 'inherit',
                    '&:hover': {
                      backgroundColor: 'action.selected'
                    }
                  }}
                >
                  {/* Month */}
                  <TableCell>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                      <span style={{fontWeight: 500}}>{monthFormatted}</span>
                      {row.loans_count > 0 && (
                        <Tooltip 
                          title='Number of loans issued this month' 
                          arrow
                        >
                          <Chip
                            label={`${row.loans_count} loans`}
                            size="small"
                            sx={{height: 18, fontSize: '10px', cursor: 'help'}}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  {/* Sales - Editable */}
                  <TableCell align="right" sx={{color: 'warning.main'}}>
                    <EditableCell
                      value={row.sales}
                      onChange={(value) => handleSalesChange(row.month, value)}
                    />
                  </TableCell>

                  {/* Expenses - Editable */}
                  <TableCell align="right" sx={{color: 'error.main'}}>
                    <EditableCell
                      value={Math.abs(row.expenses || 0)}
                      onChange={(value) => handleExpensesChange(row.month, value)}
                    />
                  </TableCell>

                  {/* Total Income */}
                  <TableCell align="right" sx={{color: 'success.dark', fontWeight: 500}}>
                    <EditableCell
                      value={row.income}
                      onChange={(value) => handleIncomeChange(row.month, value)}
                      disabled={false}
                    />
                  </TableCell>

                  {/* Cumulative Cashflow */}
                  <TableCell 
                    align="right" 
                    sx={{
                      fontWeight: 600,
                      color: row.cumulative >= 0 ? 'primary.main' : 'error.main'
                    }}
                  >
                    {formatCurrency(row.cumulative)}
                  </TableCell>

                  {/* Type */}
                  <TableCell align="center">
                    <MonthTypeChip type={row.month_type} />
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Totals Row */}
            {rows.length > 0 && (
              <TableRow sx={{backgroundColor: 'action.selected'}}>
                <TableCell sx={{fontWeight: 700}}>TOTAL</TableCell>
                <TableCell align="right" sx={{fontWeight: 700, color: 'warning.main'}}>
                  {formatCurrency(totals.sales)}
                </TableCell>
                <TableCell align="right" sx={{fontWeight: 700, color: 'error.main'}}>
                  {formatCurrency(Math.abs(totals.expenses || 0))}
                </TableCell>
                <TableCell align="right" sx={{fontWeight: 700, color: 'success.dark'}}>
                  {formatCurrency(totals.income)}
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{
                    fontWeight: 700,
                    color: totals.cumulative >= 0 ? 'primary.main' : 'error.main'
                  }}
                >
                  {formatCurrency(totals.cumulative)}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Add button
      <Box sx={{display: 'flex', justifyContent: 'center', py: 1, borderTop: '1px solid', borderColor: 'divider'}}>
        <Tooltip title="Add month after">
          <IconButton size="small" onClick={handleAddMonthAfter}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    */}
    </Card>
  );
};

export default CashflowTable;